import React, { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, MessageSquare, RefreshCcw, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import {
    deleteComment,
    fetchAdminComments,
    updateCommentStatus,
} from "../../../services/api";
import styles from "./PainelComentarios.module.css";

function formatDate(value) {
    if (!value) return "-";
    const normalized = value.endsWith("Z") ? value : `${value.replace("+00:00", "")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(date);
}

export default function PainelComentarios() {
    const { password } = useAuth();

    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [error, setError] = useState("");

    async function load() {
        try {
            setIsLoading(true);
            setError("");
            const data = await fetchAdminComments(password);
            setComments(Array.isArray(data?.comments) ? data.comments : []);
        } catch (err) {
            setError(err?.message || "Não foi possível carregar os comentários.");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [password]);

    const stats = useMemo(() => {
        return comments.reduce(
            (acc, c) => {
                acc.total += 1;
                if (c.status === "approved") acc.visible += 1;
                else acc.hidden += 1;
                return acc;
            },
            { total: 0, visible: 0, hidden: 0 }
        );
    }, [comments]);

    async function handleToggle(comment) {
        const next = comment.status === "approved" ? "rejected" : "approved";
        try {
            setBusyId(comment.id);
            setError("");
            await updateCommentStatus(password, comment.id, next);
            setComments((cur) =>
                cur.map((c) => (c.id === comment.id ? { ...c, status: next } : c))
            );
        } catch (err) {
            setError(err?.message || "Não foi possível atualizar o comentário.");
        } finally {
            setBusyId(null);
        }
    }

    async function handleDelete(comment) {
        if (confirmDelete !== comment.id) {
            setConfirmDelete(comment.id);
            setTimeout(() => {
                setConfirmDelete((cur) => (cur === comment.id ? null : cur));
            }, 3000);
            return;
        }
        try {
            setBusyId(comment.id);
            setError("");
            await deleteComment(password, comment.id);
            setComments((cur) => cur.filter((c) => c.id !== comment.id));
            setConfirmDelete(null);
        } catch (err) {
            setError(err?.message || "Não foi possível excluir o comentário.");
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className={styles.page}>
            <header className={styles.topbar}>
                <div>
                    <span className={styles.eyebrow}>Painel administrativo</span>
                    <h1>Comentários do Blog</h1>
                    <p>
                        Comentários são publicados automaticamente. Use esta tela
                        para ocultar ou excluir conteúdo indevido.
                    </p>
                </div>

                <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={load}
                    disabled={isLoading}
                >
                    <RefreshCcw size={17} aria-hidden="true" />
                    Atualizar
                </button>
            </header>

            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <span>Total</span>
                    <strong>{stats.total}</strong>
                </div>
                <div className={styles.statCard}>
                    <span>Visíveis</span>
                    <strong>{stats.visible}</strong>
                </div>
                <div className={styles.statCard}>
                    <span>Ocultos</span>
                    <strong>{stats.hidden}</strong>
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <strong>Ops!</strong>
                    <span>{error}</span>
                </div>
            )}

            {isLoading && comments.length === 0 ? (
                <p className={styles.muted}>Carregando comentários...</p>
            ) : comments.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <MessageSquare size={26} aria-hidden="true" />
                    </div>
                    <h2>Nenhum comentário ainda</h2>
                    <p>Quando os leitores comentarem, eles aparecerão aqui.</p>
                </div>
            ) : (
                <div className={styles.list}>
                    {comments.map((comment) => {
                        const hidden = comment.status !== "approved";
                        return (
                            <article
                                key={comment.id}
                                className={`${styles.card} ${hidden ? styles.cardHidden : ""}`}
                            >
                                <div className={styles.cardHead}>
                                    {comment.user_picture ? (
                                        <img
                                            src={comment.user_picture}
                                            alt={comment.user_name}
                                            className={styles.avatar}
                                            referrerPolicy="no-referrer"
                                        />
                                    ) : (
                                        <div className={styles.avatarFallback}>
                                            {(comment.user_name || "?")[0].toUpperCase()}
                                        </div>
                                    )}
                                    <div className={styles.author}>
                                        <strong>{comment.user_name}</strong>
                                        <span className={styles.authorEmail}>
                                            {comment.user_email}
                                        </span>
                                    </div>
                                    {hidden && (
                                        <span className={styles.hiddenPill}>Oculto</span>
                                    )}
                                </div>

                                <p className={styles.content}>{comment.content}</p>

                                <div className={styles.cardFooter}>
                                    <span className={styles.meta}>
                                        {comment.post_title ? (
                                            <>em <strong>{comment.post_title}</strong> · </>
                                        ) : null}
                                        {formatDate(comment.created_at)}
                                    </span>

                                    <div className={styles.actions}>
                                        <button
                                            type="button"
                                            className={styles.iconButton}
                                            onClick={() => handleToggle(comment)}
                                            disabled={busyId === comment.id}
                                            title={hidden ? "Exibir comentário" : "Ocultar comentário"}
                                        >
                                            {hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                                            {hidden ? "Exibir" : "Ocultar"}
                                        </button>

                                        <button
                                            type="button"
                                            className={`${styles.deleteButton} ${
                                                confirmDelete === comment.id ? styles.deleteConfirm : ""
                                            }`}
                                            onClick={() => handleDelete(comment)}
                                            disabled={busyId === comment.id}
                                            title={
                                                confirmDelete === comment.id
                                                    ? "Clique novamente para confirmar"
                                                    : "Excluir comentário"
                                            }
                                        >
                                            <Trash2 size={16} />
                                            {confirmDelete === comment.id
                                                ? busyId === comment.id
                                                    ? "Excluindo..."
                                                    : "Confirmar?"
                                                : "Excluir"}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
